import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import usePagination from "../../hooks/usePagination";
import "./PaymentBatches.css";

const formatDateMonthDayYear = (dateInput?: string | Date | null): string => {
  if (!dateInput) return "-";

  if (typeof dateInput === "string") {
    const match = dateInput.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${months[monthIdx]} ${day}, ${year}`;
      }
    }
  }

  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);

  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/* SVG icons — stroke colour driven by CSS `color` (currentColor) */
const ChevronRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
  </svg>
);

const STATUS_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "PARTIALLY_PROCESSED", label: "Partially Issued" },
  { value: "COMPLETED", label: "Complete" },
];


const PaymentBatches: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Two-tier search state — same pattern as admin dashboard Stores page:
  //   inputValue  = what the user is typing (live, unsubmitted)
  //   appliedSearch = the term actually sent to the API (set on Enter / cleared by ×)
  const [inputValue, setInputValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUrl = useMemo(() => {
    const base = `${import.meta.env.VITE_API_URL}/payments/batches`;
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== "ALL" && statusFilter !== "PARTIALLY_PROCESSED") {
      params.append("status", statusFilter);
    }
    if (appliedSearch.trim()) {
      params.append("search", appliedSearch.trim());
    }
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [statusFilter, appliedSearch]);

  const {
    data: batches,
    isLoading,
    isLoadingMore,
    error,
    errorContent,
    hasMore,
    hasFetched,
    fetchInitial,
    fetchMore,
  } = usePagination({
    url: fetchUrl,
    method: "GET",
    listKey: "batches",
  });

  // Disable browser scroll restoration so refresh always starts at top
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchInitial();
    window.scrollTo(0, 0);
  }, [location, fetchUrl, fetchInitial]);

  const hasMoreRef = useRef(hasMore);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const isLoadingMoreRef = useRef(isLoadingMore);
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);

  const isFetchingRef     = useRef(false);
  const isIntersectingRef = useRef(false);
  const cooldownTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const fetchMoreBatches = useCallback(async () => {
    if (!hasMoreRef.current || isLoadingMoreRef.current || isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      await fetchMore();
    } finally {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => {
        isFetchingRef.current = false;
        if (isIntersectingRef.current && hasMoreRef.current && !isLoadingMoreRef.current) {
          fetchMoreBatches();
        }
      }, 400);
    }
  }, [fetchMore]);

  const observerTargetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!hasMore || !hasFetched || isLoading || batches.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0]?.isIntersecting ?? false;
        isIntersectingRef.current = isIntersecting;
        if (isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current && !isFetchingRef.current && !isLoading) {
          fetchMoreBatches();
        }
      },
      { rootMargin: "100px", threshold: 0.1 }
    );

    const currentTarget = observerTargetRef.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => observer.disconnect();
  }, [hasMore, hasFetched, isLoading, batches.length, fetchMoreBatches]);

  /* Submit current inputValue as the applied search → triggers fetchUrl change → re-fetch */
  const handleSearchSubmit = () => {
    setAppliedSearch(inputValue);
  };

  /* Clear both input and applied search → triggers re-fetch with no search term */
  const handleSearchClear = () => {
    setInputValue("");
    setAppliedSearch("");
  };

  /* Status chip — matches mockup chip colour variants */
  const getBatchStatusInfo = (batch: any) => {
    const completedCount =
      batch.issued_stores_count !== undefined
        ? batch.issued_stores_count
        : (batch.store_releases || []).filter(
            (r: any) => r.status === "CHECK_ISSUED" || r.status === "COMPLETED"
          ).length;
    const totalCount =
      batch.total_stores ||
      (batch.store_ids ? batch.store_ids.length : 0) ||
      (batch.store_releases || []).length ||
      0;

    if (batch.status === "COMPLETED")  return { label: "Complete",  chipClass: "pb-chip--complete"  };
    if (batch.status === "CANCELLED")  return { label: "Cancelled", chipClass: "pb-chip--cancelled" };
    if (batch.status === "PARTIALLY_PROCESSED" || (completedCount > 0 && completedCount < totalCount)) {
      return { label: `${completedCount} of ${totalCount} issued`, chipClass: "pb-chip--partial" };
    }
    return { label: "Pending", chipClass: "pb-chip--pending" };
  };

  /* Normalise batches array from API response */
  const batchList: any[] = useMemo(() => {
    if (Array.isArray(batches)) return batches;
    if (batches && Array.isArray((batches as any).batches)) return (batches as any).batches;
    return [];
  }, [batches]);

  /* Client-side status filter only — search is fully server-side now */
  const filteredBatches = useMemo(() => {
    return batchList.filter((batch: any) => {
      const { label: statusLabel } = getBatchStatusInfo(batch);
      return (
        statusFilter === "ALL" ||
        (statusFilter === "PENDING"             && batch.status === "PENDING") ||
        (statusFilter === "PARTIALLY_PROCESSED" && (batch.status === "PARTIALLY_PROCESSED" || statusLabel.includes("issued"))) ||
        (statusFilter === "COMPLETED"           && batch.status === "COMPLETED") ||
        (statusFilter === "CANCELLED"           && batch.status === "CANCELLED")
      );
    });
  }, [batchList, statusFilter]);


  /* Skeleton rows — all visual spacing via CSS classes, no inline styles */
  const renderSkeletonRows = (count: number) =>
    Array.from({ length: count }, (_, i) => (
      <tr key={i} className="pb-skeleton-row">
        <td className="pb-skeleton-td pb-col-batch">
          <span className="pb-skeleton-line pb-skeleton-line--short" />
        </td>
        <td className="pb-skeleton-td pb-col-created pb-td-created">
          <span className="pb-skeleton-line pb-skeleton-line--med" />
          <span className="pb-skeleton-line pb-skeleton-line--short pb-skeleton-line--gap" />
        </td>
        <td className="pb-skeleton-td pb-col-stores">
          <span className="pb-skeleton-line pb-skeleton-line--short" />
          <span className="pb-skeleton-line pb-skeleton-line--gap" />
        </td>
        <td className="pb-skeleton-td pb-td-num pb-td-amount pb-col-amount">
          <span className="pb-skeleton-line pb-skeleton-line--short pb-skeleton-line--right" />
        </td>
        <td className="pb-skeleton-td pb-td-checks pb-col-checks">
          <span className="pb-skeleton-line pb-skeleton-line--short" />
        </td>
        <td className="pb-skeleton-td pb-col-status">
          <span className="pb-skeleton-line pb-skeleton-line--short" />
        </td>
        <td className="pb-skeleton-td pb-td-actions pb-col-open" />
      </tr>
    ));

  if (error?.code === 401) {
    return (
      <div className="account-section w-clearfix stores-section">
        <div className="div-block-28">
          <div className="pb-page">
            <div className="pb-empty">{errorContent}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-section w-clearfix stores-section">
      <div className="div-block-28">
        <div className="pb-page">
          {/* —— Page Head —— */}
      <div className="pb-page-head">
        <div>
          <h1 className="pb-page-title">Payment Batches</h1>
          <div className="pb-page-sub">All payments by check</div>
        </div>

        {/* —— Toolbar: Search + Status Filter —— */}
        <div className="pb-toolbar">
          {/* Search field — type and press Enter to search, × to clear */}
          <label className="pb-search">
            <span className="pb-search-icon">
              <SearchIcon />
            </span>
            <span className="pb-sr-only">Search batches</span>
            <input
              type="search"
              id="searchBatches"
              className="pb-search-input"
              placeholder="Search batch # or store"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchSubmit();
                }
              }}
              aria-label="Search payment batches"
            />
            {inputValue && (
              <button
                type="button"
                className="pb-search-clear-btn"
                onClick={handleSearchClear}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </label>


          {/* Custom Status filter */}
          <div className="pb-select-group" ref={dropdownRef}>
            <span className="pb-select-label-text">Status</span>
            <div className="pb-custom-select">
              <button
                type="button"
                className={`pb-custom-select-trigger ${isDropdownOpen ? "pb-custom-select-trigger--open" : ""}`}
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                id="status-filter-trigger"
              >
                <span className="pb-custom-select-val">
                  {STATUS_OPTIONS.find((opt) => opt.value === statusFilter)?.label || "All statuses"}
                </span>
                <span className={`pb-custom-select-arrow ${isDropdownOpen ? "pb-custom-select-arrow--open" : ""}`}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>

              {isDropdownOpen && (
                <ul
                  className="pb-custom-select-menu"
                  role="listbox"
                  aria-labelledby="status-filter-trigger"
                >
                  {STATUS_OPTIONS.map((opt) => {
                    const isSelected = statusFilter === opt.value;
                    return (
                      <li
                        key={opt.value}
                        role="option"
                        aria-selected={isSelected}
                        className={`pb-custom-select-option ${isSelected ? "pb-custom-select-option--selected" : ""}`}
                        onClick={() => {
                          setStatusFilter(opt.value);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <span className="pb-custom-select-option-text">{opt.label}</span>

                        {isSelected && (
                          <span className="pb-custom-select-check">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* —— Table Card —— */}
      <section className="pb-card pb-table-wrap">
        <table className="pb-table">
          <thead>
            <tr>
              <th className="pb-col-batch">Batch</th>
              <th className="pb-col-created">Created</th>
              <th className="pb-col-stores">Stores</th>
              <th className="pb-th-num pb-col-amount">Amount</th>
              <th className="pb-col-checks">Checks issued</th>
              <th className="pb-col-status">Status</th>
              <th className="pb-th-sr pb-col-open">Open</th>
            </tr>
          </thead>
          <tbody>
            {/* Loading skeleton */}
            {isLoading && !hasFetched && renderSkeletonRows(4)}

            {/* Error state */}
            {hasFetched && errorContent && (
              <tr className="pb-empty-row">
                <td colSpan={7} className="pb-empty">{errorContent}</td>
              </tr>
            )}

            {/* Empty state */}
            {hasFetched && !errorContent && filteredBatches.length === 0 && (
              <tr className="pb-empty-row">
                <td colSpan={7} className="pb-empty">
                  {statusFilter !== "ALL" || appliedSearch.trim()
                    ? "No payment batches match your filter criteria."
                    : "No payment batches available yet."}
                </td>
              </tr>
            )}

            {/* Data rows */}
            {!isLoading &&
              filteredBatches.map((batch: any) => {
                const createdDate  = formatDateMonthDayYear(batch.createdAt);
                const creatorEmail = batch.creator?.email || "admin@justforkix.com";

                const storeCount =
                  batch.total_stores ||
                  (batch.store_ids ? batch.store_ids.length : 0) ||
                  (batch.store_releases || []).length ||
                  0;

                const storeList: { id: number | string; name: string }[] =
                  batch.stores ||
                  (batch.store_releases || []).map((r: any) => ({
                    id: r.store_id,
                    name: r.store?.name || `Store #${r.store_id}`,
                  })) ||
                  (batch.store_ids || []).map((id: any) => ({ id, name: `Store #${id}` }));

                const storeNames  = storeList.map((s) => s.name).join(", ");
                const issuedCount = batch.issued_stores_count !== undefined
                  ? batch.issued_stores_count
                  : (batch.store_releases || []).filter(
                      (r: any) => r.status === "CHECK_ISSUED" || r.status === "COMPLETED"
                    ).length;

                const totalAmount = batch.total_amount != null
                  ? `$${Number(batch.total_amount).toFixed(2)}`
                  : "-";

                const { label: statusLabel, chipClass } = getBatchStatusInfo(batch);

                return (
                  <tr
                    key={batch.id}
                    className="pb-data-row"
                    onClick={() => navigate(`/payment-batches/${batch.id}`)}
                  >
                    {/* Batch # */}

                    <td className="pb-col-batch">
                      <Link to={`/payment-batches/${batch.id}`} className="pb-cell-primary">
                        #{batch.id}
                      </Link>
                    </td>

                    {/* Created + creator */}
                    <td className="pb-col-created pb-td-created">
                      {createdDate}
                      <div className="pb-cell-sub">by {creatorEmail}</div>
                    </td>

                    {/* Stores count + names */}
                    <td className="pb-col-stores">
                      {storeCount} {storeCount === 1 ? "store" : "stores"}
                      {storeNames && (
                        <div className="pb-cell-sub">{storeNames}</div>
                      )}
                    </td>

                    {/* Amount */}
                    <td className="pb-td-num pb-td-amount pb-col-amount pb-cell-strong">
                      <span className="pb-mobile-label">Amount</span>
                      {totalAmount}
                    </td>

                    {/* Checks issued */}
                    <td className="pb-td-checks pb-col-checks">
                      <span className="pb-mobile-label">Checks</span>
                      {issuedCount}{" "}
                      <span className="pb-cell-faint">of {storeCount}</span>
                    </td>

                    {/* Status chip */}
                    <td className="pb-col-status">
                      <span className={`pb-chip ${chipClass}`}>{statusLabel}</span>
                    </td>

                    {/* Open chevron */}
                    <td className="pb-td-actions pb-col-open">
                      <Link
                        to={`/payment-batches/${batch.id}`}
                        className="pb-icon-btn"
                        aria-label={`Open batch ${batch.id}`}
                      >
                        <ChevronRight />
                      </Link>
                    </td>
                  </tr>
                );
              })}

            {/* Loading more skeletons */}
            {isLoadingMore && renderSkeletonRows(3)}
          </tbody>
        </table>
      </section>

      {/* Infinite scroll trigger */}
      {hasFetched && hasMore && (
        <div ref={observerTargetRef} className="pb-infinite-trigger" />
      )}

      {/* Load More fallback */}
      {hasMore && !isLoadingMore && (
        <div className="pb-load-more-wrapper">
          <button className="pb-load-more-btn" onClick={fetchMoreBatches}>
            Load more
          </button>
        </div>
      )}

        {hasFetched && !errorContent && filteredBatches.length > 0 && (
          <div className="pb-footer-sub">
            Showing {filteredBatches.length}{" "}
            {filteredBatches.length === 1 ? "batch" : "batches"}
            {appliedSearch.trim() ? ` matching "${appliedSearch.trim()}"` : ", newest first"}
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default PaymentBatches;
